/**
 * Compact Auto Sourcing scan dashboard (Stage L).
 *
 * Layout matches the approved reference: one compact retailer header row
 * (identity · timer · controls), a single thin time-progress bar, a live
 * activity line, and five inline metrics — no oversized cards, no empty gaps.
 *
 * Every value comes from persisted backend counters. No fabricated progress.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Clock,
  Database,
  Folder,
  Pause,
  Play,
  RotateCw,
  Square,
} from "lucide-react";
import RetailerLogo from "./RetailerLogo";
import { WalmartLogoMark } from "@/components/common/WalmartLogoMark";
import { cn } from "@/lib/utils";
import {
  compactNumber,
  formatDuration,
  formatStartedAt,
  phasePresentation,
  queueWaitSeconds,
  remainingDurationSeconds,
  statusPresentation,
  timerState,
} from "./runSummaryFormat";
import { useAnimatedNumber } from "./useAnimatedNumber";

/** Logos that crop poorly with cover — keep aspect via contain. */
const CONTAIN_LOGO_IDS = new Set(["michaels_com"]);

/** Header marketplace mark — the Walmart logo, never a bare yellow star. */
function WalmartSpark() {
  return <WalmartLogoMark size={13} />;
}

/** Moving cart marker for the scan progress bar. */
function CartIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="9" cy="20" r="1" />
      <circle cx="19" cy="20" r="1" />
      <path d="M2 3h2l3 13h13l2-9H5" />
    </svg>
  );
}

function resolveRunSources(runSourceIds, sources) {  return runSourceIds.map((id) => {
    const found = sources.find((s) => s.source_id === id);
    return found || { source_id: id, name: id };
  });
}

function FaviconRow({ sources, multi, fallbackName }) {
  if (!multi) {
    const source = sources[0] || { name: fallbackName || "Store" };
    const objectFit = CONTAIN_LOGO_IDS.has(source.source_id) ? "contain" : "cover";
    return (
      <div className="as-run-brand" aria-hidden="true" data-testid="as-run-logo">
        <RetailerLogo source={source} size="md" variant="bleed" objectFit={objectFit} />
      </div>
    );
  }

  const visible = sources.slice(0, 5);
  const extra = Math.max(0, sources.length - 5);
  const names = sources.map((s) => s.name || s.source_id).join(", ");

  return (
    <div
      className="as-run-favicon-row"
      data-testid="as-run-retailer-stack"
      title={names}
      aria-label={names}
    >
      {visible.map((s) => (
        <span key={s.source_id} className="as-run-favicon-item">
          <RetailerLogo
            source={s}
            size="md"
            variant="bleed"
            objectFit={CONTAIN_LOGO_IDS.has(s.source_id) ? "contain" : "cover"}
          />
        </span>
      ))}
      {extra > 0 ? (
        <span className="as-run-favicon-more" data-testid="as-run-favicon-more" title={names}>
          +{extra}
        </span>
      ) : null}
    </div>
  );
}

/** One inline metric with a smoothly animated number. */
function Metric({ label, value, format = compactNumber, testId, suffix, highlight }) {
  const animated = useAnimatedNumber(value);
  const previous = React.useRef(value);
  const [bumped, setBumped] = useState(false);

  useEffect(() => {
    if (previous.current === value) return undefined;
    previous.current = value;
    setBumped(true);
    const t = setTimeout(() => setBumped(false), 600);
    return () => clearTimeout(t);
  }, [value]);

  return (
    <div
      className={cn("as-metric", highlight && "is-highlight", bumped && "is-bumped")}
      data-testid={testId}
    >
      <span className="as-metric-label">{label}</span>
      <span className="as-metric-value tabular-nums">
        {format(animated)}
        {suffix ? <span className="as-metric-suffix">{suffix}</span> : null}
      </span>
    </div>
  );
}

/** Real activity from backend state — never invented. */
export function activityMessage(run, timer) {
  const status = String(run?.status || "").toLowerCase();
  const progress = run?.progress || {};
  const collected = Number(progress.products_collected) || 0;
  const pages = Number(progress.listing_pages_scanned) || 0;
  const stage = String(progress.current_stage || "").replace(/_/g, " ");
  const remaining = timer?.remaining;

  if (status === "paused") {
    return remaining != null
      ? `Paused — ${formatDuration(remaining)} remaining`
      : "Paused";
  }
  if (status === "failed") {
    // Actionable failure text from the backend, never a fake active scan.
    const message = String(run?.customer_message || "").trim();
    if (message) return message;
    const label = statusPresentation(status, run?.stop_reason).label;
    return label
      ? `${label} — no products were collected.`
      : "Scan stopped — no products were collected.";
  }
  if (["completed", "partial", "cancelled"].includes(status)) {
    return statusPresentation(status, run?.stop_reason).label;
  }
  if (status === "queued") return "Queued — waiting for a worker";
  if (status === "matching" || /matching/.test(stage)) {
    const pending = Number(progress.pending_match_backlog);
    return Number.isFinite(pending) && pending > 0
      ? `Checking Walmart candidate matches… ${pending} awaiting match`
      : "Checking Walmart candidate matches…";
  }
  if (status === "verifying" || /verifying/.test(stage)) {
    return "Checking Walmart candidate matches…";
  }
  // Stage M: a specific recovery message beats the generic stage line.
  const recoveryText = String(run?.category_recovery?.text || "").trim();
  if (recoveryText && !collected) {
    return recoveryText;
  }
  const hostRepair = Array.isArray(run?.host_repairs) ? run.host_repairs.slice(-1)[0] : null;
  if (hostRepair && !collected) {
    return "Category URL corrected automatically.";
  }
  if (/collecting|reading/.test(stage) || status === "collecting") {
    const parts = [];
    if (pages > 0) parts.push(`Reading listing page ${pages}`);
    if (collected > 0) parts.push(`${collected} products collected`);
    if (collected > 0) parts.push("Matching with Walmart");
    return parts.length ? `Scanning: ${parts.join(" · ")}` : "Waiting for retailer response…";
  }
  return collected > 0 ? `${collected} products collected` : "Starting scan…";
}

export default function RunSummaryCard({
  run,
  sources = [],
  onRefresh,
  onViewHistory,
  onResume,
  onPause,
  onStop,
  activityExtra,
  durableCounts,
}) {
  const progress = run?.progress || {};
  const collected = Number(progress.products_collected) || 0;
  const discovered = Number(progress.products_discovered) || collected;
  const evaluated =
    Number(progress.products_evaluated) || Number(run?.products_evaluated) || 0;
  // Durable Results counters are authoritative; the job's progress counters can
  // lag behind (they count qualification outcomes, not identity matches). The
  // metric must agree with the "Walmart Matches" filter chip beside it.
  const durableMatched =
    durableCounts && Number.isFinite(Number(durableCounts.matched))
      ? Number(durableCounts.matched)
      : null;
  const durablePotential =
    durableCounts && Number.isFinite(Number(durableCounts.potential))
      ? Number(durableCounts.potential)
      : null;
  const matched = durableMatched ?? (Number(progress.matched) || 0);
  const potential =
    durablePotential
    ?? (Number(progress.potential) || Number(run?.result_counts?.potential) || 0);
  const walmartMatches = matched + potential;
  const catsDone = Number(progress.categories_scanned) || 0;
  const catsTotal = Number(progress.categories_total) || 0;
  const requestsUsed = Number(
    progress.aggregate_provider_calls ?? run?.aggregate_provider_calls ?? progress.provider_calls,
  ) || 0;
  const requestsLimit = Number(
    progress.match_budget_limit ?? run?.provider_request_limit,
  ) || 0;
  // Stage X: paid provider requests vs. free direct retailer fetches. A running
  // job with 0 paid requests is NOT idle — direct HTTP collection costs nothing,
  // so the two must never be collapsed into one misleading number.
  const reqBreakdown = run?.requests && typeof run.requests === "object" ? run.requests : null;
  const paidRequests = reqBreakdown && Number.isFinite(Number(reqBreakdown.paid_provider))
    ? Number(reqBreakdown.paid_provider)
    : requestsUsed;
  const paidLimit = reqBreakdown && Number.isFinite(Number(reqBreakdown.paid_limit))
    ? Number(reqBreakdown.paid_limit)
    : requestsLimit;
  const directPages = reqBreakdown && Number.isFinite(Number(reqBreakdown.direct_retailer_pages))
    ? Number(reqBreakdown.direct_retailer_pages)
    : 0;
  const walmartCalls = reqBreakdown && Number.isFinite(Number(reqBreakdown.walmart_api))
    ? Number(reqBreakdown.walmart_api)
    : 0;
  const showDirectHttp = Boolean(reqBreakdown) && (directPages > 0 || requestsUsed === 0);

  const runSourceIds = Array.isArray(run?.source_ids) ? run.source_ids.filter(Boolean) : [];
  const runSources = resolveRunSources(runSourceIds, sources);
  const multiRetailer = runSourceIds.length > 1;
  const retailerName = multiRetailer
    ? "Multi-Retailer Search"
    : runSources[0]?.name || runSourceIds[0] || run?.name || "Auto Sourcing";

  const categoryLabel = multiRetailer
    ? `${runSourceIds.length} retailers`
    : catsTotal > 0
      ? catsTotal === 1
        ? "1 category"
        : `${catsTotal} categories`
      : "All categories";

  const status = statusPresentation(run?.status, run?.stop_reason);
  const statusKey = String(run?.status || "").toLowerCase();
  const queued = statusKey === "queued";
  const running = ["running", "collecting", "matching", "verifying"].includes(statusKey);
  const paused = statusKey === "paused";
  const finished = ["completed", "partial", "failed", "cancelled"].includes(statusKey);
  const canPause = (running || queued) && typeof onPause === "function";

  // 1s tick keeps the timer honest while the scan is active. The tick value is
  // part of the timer memo so progress advances between server polls. A queued
  // job ticks too, but only its "waiting" line moves — never the scan window.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!running && !queued) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [running, queued, run?.run_id]);

  const nowMs = useMemo(() => Date.now(), [run, tick]);
  // Explicit phase vocabulary (Queued / Starting / Running / Waiting for
  // retailer / Paused / Completed / Cancelled / Failed) from persisted facts.
  const phase = phasePresentation(run);
  // Queue wait is shown on its own; it never counts toward the scan window.
  const waitedSeconds = queueWaitSeconds(run, nowMs);
  const queuePosition = Number(run?.queue?.position) || null;

  const timer = useMemo(() => timerState(run, nowMs), [run, nowMs]);
  const elapsedLabel =
    timer.authorized != null
      ? `${formatDuration(timer.consumed)} / ${formatDuration(timer.authorized)}`
      : formatDuration(timer.consumed);
  const remainingSec = remainingDurationSeconds(run, nowMs);
  const remainingLabel =
    !finished && remainingSec != null ? `Time remaining — ${formatDuration(remainingSec)}` : null;
  // Queue wait is its own visible fact, never folded into the scan window.
  const queueLine = queued && waitedSeconds != null
    ? `Waiting for an available worker — ${formatDuration(waitedSeconds)}${
        queuePosition && queuePosition > 1 ? ` · position ${queuePosition}` : ""
      }`
    : null;
  const started = formatStartedAt(run?.started_at || run?.created_at);
  const serverActivity =
    !finished && typeof run?.activity === "string" && run.activity.trim()
      ? run.activity.trim()
      : null;
  // When collection is happening over direct HTTP, say so. Otherwise "0 paid
  // provider requests" reads as "nothing is happening" while pages are being
  // fetched. Only appended when the server has not already said it.
  const directNote =
    paidRequests === 0 && directPages > 0 && !/direct/i.test(serverActivity || "")
      ? "no paid provider requests"
      : null;
  const localActivity = activityMessage(run, timer);
  const baseActivity =
    queueLine
    || serverActivity
    || (directNote ? `${localActivity} · ${directNote}` : localActivity);
  // The dashboard activity line IS the live indicator — no second live bar.
  const activity = activityExtra ? `${baseActivity} · ${activityExtra}` : baseActivity;

  // Time progress only; never the share of products discovered.
  const indeterminate = timer.percent == null && running;
  const barPct = timer.percent == null ? (running ? 28 : 0) : timer.percent;
  // This bar measures TIME USED against the authorized window — it is not a
  // measure of work completed. A scan that finishes its available work early
  // legitimately shows a low percentage, and a bare "19%" beside a completed
  // run reads as "19% of the work was done". Label the terminal case explicitly
  // so the number cannot be misread. Nothing is recomputed or inflated.
  const pctLabel =
    timer.percent == null
      ? null
      : timer.terminal
        ? `${Math.round(timer.percent)}% of window used`
        : `${Math.round(timer.percent)}%`;
  // Keep the cart inside the track at both ends (plain values so every browser
  // and jsdom parse the style).
  const cartLeft =
    barPct >= 99
      ? "calc(100% - 10px)"
      : barPct <= 1
        ? "10px"
        : `${barPct}%`;

  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="as-run-card" data-testid="as-scan-dashboard">
      <div className="as-run-head">
        <div className="as-run-identity">
          <FaviconRow sources={runSources} multi={multiRetailer} fallbackName={retailerName} />
          <div className="as-run-identity-text">
            <h2 className="as-run-title" data-testid="auto-sourcing-run-title">
              {retailerName}
            </h2>
            <div className="as-run-subline" data-testid="as-run-subline">
              <span className="as-run-meta-item">
                <Folder className="as-run-meta-svg" aria-hidden="true" />
                {categoryLabel}
              </span>
              <span className="as-run-sep" aria-hidden="true">·</span>
              <span className="as-run-meta-item as-run-meta-walmart" data-testid="as-run-marketplace">
                <WalmartSpark />
                <span>Walmart US</span>
              </span>
              {started ? (
                <>
                  <span className="as-run-sep" aria-hidden="true">·</span>
                  <span className="as-run-meta-item">{started}</span>
                </>
              ) : null}
            </div>
          </div>
        </div>

        <div className="as-run-center">
          <span
            className={cn("as-run-status", `as-run-status-${status.tone}`)}
            data-testid="as-run-status"
          >
            {running ? <span className="as-run-status-dot" aria-hidden="true" /> : null}
            {status.label}
          </span>
          <span className="as-run-timer" data-testid="as-run-elapsed">
            <Clock className="as-run-meta-svg" aria-hidden="true" />
            {elapsedLabel}
          </span>
          {remainingLabel && !queued ? (
            <span className="as-run-remaining" data-testid="as-run-remaining">
              {remainingLabel}
            </span>
          ) : null}
          {queueLine ? (
            <span className="as-run-remaining" data-testid="as-run-queue">
              {queueLine}
            </span>
          ) : null}
        </div>

        <div className="as-run-actions">
          {paused && typeof onResume === "function" ? (
            <button
              type="button"
              className="as-ctrl as-ctrl-primary"
              onClick={onResume}
              data-testid="as-resume-scan"
              title="Resume scan"
            >
              <Play /> Resume
            </button>
          ) : canPause ? (
            <button
              type="button"
              className="as-ctrl"
              onClick={onPause}
              data-testid="as-pause-scan"
              title="Pause scan — in-flight requests finish, progress is saved"
            >
              <Pause /> Pause
            </button>
          ) : null}
          {typeof onStop === "function" && (running || paused) ? (
            <button
              type="button"
              className="as-ctrl as-ctrl-danger"
              onClick={onStop}
              data-testid="as-stop-scan"
              title="Stop scan"
            >
              <Square /> Stop
            </button>
          ) : null}
          {!running && !paused && run?.resume_available && typeof onResume === "function" ? (
            <button
              type="button"
              className="as-ctrl"
              onClick={onResume}
              data-testid="as-resume-scan"
              title="Resume scan"
            >
              <Play /> Resume
            </button>
          ) : null}
          {typeof onRefresh === "function" ? (
            <button
              type="button"
              className="as-ctrl as-ctrl-icon"
              onClick={onRefresh}
              aria-label="Refresh"
              title="Refresh"
              data-testid="as-refresh-scan"
            >
              <RotateCw />
            </button>
          ) : null}
          <button
            type="button"
            className="as-ctrl as-ctrl-icon"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? "Expand scan details" : "Collapse scan details"}
            title={collapsed ? "Expand" : "Collapse"}
            data-testid="as-toggle-details"
          >
            {collapsed ? <ChevronDown /> : <ChevronUp />}
          </button>
        </div>
      </div>

      <div
        className={cn(
          "as-run-progress",
          running && "as-run-progress-running",
          indeterminate && "as-run-progress-indeterminate",
          finished && "as-run-progress-finished",
        )}
        role="progressbar"
        aria-label="Scan time progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={timer.percent == null ? undefined : Math.round(timer.percent)}
        aria-valuetext={elapsedLabel}
        data-testid="as-run-progress"
      >
        <div className="as-run-track">
          <div
            className={cn("as-run-fill", (running || paused) && !finished && "as-run-fill-animated")}
            style={{ width: `${barPct}%` }}
            data-testid="as-run-fill"
          />
          {(running || paused) && !finished ? (
            <span
              className="as-run-cart"
              style={{ left: cartLeft }}
              data-testid="as-run-cart"
              aria-hidden="true"
            >
              <CartIcon />
            </span>
          ) : null}
        </div>
        {pctLabel ? (
          <span className="as-run-pct" data-testid="as-run-pct">
            {pctLabel}
          </span>
        ) : null}
      </div>

      <div
        className="as-activity"
        data-testid={finished ? "as-stop-reason" : "as-run-activity"}
      >
        <span
          className={cn("as-activity-dot", paused && "is-paused", finished && "is-idle")}
          aria-hidden="true"
        />
        <span className="as-activity-text">{activity}</span>
      </div>

      {collapsed ? null : (
        <div className="as-metrics" aria-label="Run statistics" data-testid="as-run-stats">
          <Metric
            label="Products Discovered"
            value={discovered}
            testId="as-run-stat-discovered"
          />
          <Metric label="Products Evaluated" value={evaluated} testId="as-run-stat-evaluated" />
          {/* Both counters are valid but were both called "Walmart Matches":
              the run summary counted VERIFIED matches while the Results tab
              counted verified + potential. A production run reported "8 matches"
              and "11 matches" for the same job, which reads as a bug. The label
              now names the set, and the breakdown is visible. */}
          <Metric
            label="All Matches"
            value={walmartMatches}
            title={`${matched} verified · ${potential} potential`}
            testId="as-run-stat-walmart-matches"
          />
          <div className="as-metric" data-testid="as-run-stat-match-breakdown">
            <span className="as-metric-label">Verified / Potential</span>
            <span className="as-metric-value tabular-nums">
              {matched} / {potential}
            </span>
          </div>
          <div className="as-metric" data-testid="as-run-stat-categories">
            <span className="as-metric-label">Categories Progress</span>
            <span className="as-metric-value tabular-nums">
              {catsTotal > 0 ? `${catsDone} / ${catsTotal}` : "—"}
            </span>
          </div>
          <div className="as-metric" data-testid="as-run-stat-provider-requests">
            <span className="as-metric-label">Paid Provider Requests</span>
            <span className="as-metric-value tabular-nums">
              <AnimatedRequests used={paidRequests} />
              {paidLimit > 0 ? (
                <span className="as-metric-suffix"> / {compactNumber(paidLimit)}</span>
              ) : null}
            </span>
          </div>
          {showDirectHttp ? (
            <div className="as-metric" data-testid="as-run-stat-direct-pages">
              <span className="as-metric-label">Retailer Pages (direct)</span>
              <span className="as-metric-value tabular-nums">
                <AnimatedRequests used={directPages} />
              </span>
            </div>
          ) : null}
          {showDirectHttp && walmartCalls > 0 ? (
            <div className="as-metric" data-testid="as-run-stat-walmart-lookups">
              <span className="as-metric-label">Walmart Lookups</span>
              <span className="as-metric-value tabular-nums">
                <AnimatedRequests used={walmartCalls} />
              </span>
            </div>
          ) : null}
        </div>
      )}

      {collapsed ? null : (
        <CategoryStatusLine progress={progress} testId="as-run-category-status" />
      )}
    </div>
  );
}

/**
 * Four auditable category states — deliberately NOT one "exhausted" count.
 *
 * A category we merely stopped probing used to be reported as exhausted, which
 * made a partial scan look like a completed one. These labels keep the
 * difference visible: only "Pagination end verified" claims we reached the end.
 */
export function CategoryStatusLine({ progress, testId = "as-run-category-status" }) {
  const p = progress || {};
  const rows = [
    {
      key: "completed",
      label: "Pagination end verified",
      value: Number(p.categories_completed) || 0,
    },
    {
      key: "incomplete",
      label: "Next page unproven",
      value: Number(p.categories_incomplete) || 0,
    },
    {
      key: "interrupted",
      label: "Resumable cursor exists",
      value: Number(p.categories_interrupted) || 0,
    },
    {
      key: "restricted",
      label: "Acquisition blocked",
      value: Number(p.categories_restricted) || 0,
    },
  ];
  // Nothing to say until the backend reports the vocabulary (older runs).
  if (!rows.some((r) => r.value > 0)) return null;
  return (
    <div className="as-category-status" data-testid={testId} aria-label="Category status">
      {rows.map((r) => (
        <span
          key={r.key}
          className={`as-category-status-chip as-category-status-${r.key}`}
          data-testid={`${testId}-${r.key}`}
          title={r.label}
        >
          <span className="as-category-status-count tabular-nums">{r.value}</span>
          <span className="as-category-status-label">{r.label}</span>
        </span>
      ))}
    </div>
  );
}

/** Only the consumed count animates; the authorized total stays fixed. */
function AnimatedRequests({ used }) {
  const animated = useAnimatedNumber(used);
  return <>{compactNumber(animated)}</>;
}

/** Neutral skeleton cards while a scan is collecting and no outcomes yet. */
export function ResultsSkeletonGrid({ count = 4 }) {
  return (
    <div
      className="df-grid grid gap-2.5 grid-cols-[repeat(auto-fill,minmax(212px,1fr))] pb-4"
      data-testid="as-results-skeleton"
      aria-hidden="true"
    >
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="as-skel-card">
          <div className="as-skel-media">
            <div className="as-skel-block as-skel-img" />
            <div className="as-skel-block as-skel-img" />
          </div>
          <div className="as-skel-block as-skel-line" />
          <div className="as-skel-block as-skel-line as-skel-short" />
          <div className="as-skel-metrics">
            <div className="as-skel-block as-skel-chip" />
            <div className="as-skel-block as-skel-chip" />
            <div className="as-skel-block as-skel-chip" />
          </div>
        </div>
      ))}
    </div>
  );
}
